import React, { useEffect, useRef, useState } from "react";
import { FaPause, FaPlay, FaTimes, FaUpload } from "react-icons/fa";
import { MdVideocam, MdRefresh } from "react-icons/md";

import { createComment, createReply } from "../../api";
import ToastMessage from "../ToastMessage";

const MAX_DURATION = 15;

const VideoComposer = ({ mode, postId, postType, type, comment, close, closeReply }) => {
  const cameraVideoRef = useRef(null);
  const previewVideoRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const recordingStartedAtRef = useRef(null);

  const [file, setFile] = useState(null);
  const [videoSource, setVideoSource] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordedDuration, setRecordedDuration] = useState(0);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const [previewCurrentTime, setPreviewCurrentTime] = useState(0);
  const [previewDuration, setPreviewDuration] = useState(0);

  const isReply = mode === "reply";

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) return "00:00";

    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  };

  const effectivePreviewDuration = Number.isFinite(previewDuration) && previewDuration > 0 ? previewDuration : recordedDuration > 0 ? recordedDuration : 0;

  const clearPreview = () => {
    if (previewVideoRef.current) previewVideoRef.current.pause();
    if (previewUrl) URL.revokeObjectURL(previewUrl);

    setPreviewUrl("");
    setPreviewCurrentTime(0);
    setPreviewDuration(0);
    setIsPreviewPlaying(false);
  };

  const clearRecordingTimer = () => {
    if (!timerRef.current) return;

    clearInterval(timerRef.current);
    timerRef.current = null;
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (cameraVideoRef.current) cameraVideoRef.current.srcObject = null;

    setCameraOpen(false);
  };

  const checkUploadedVideoDuration = (selectedFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const url = URL.createObjectURL(selectedFile);

      video.preload = "metadata";

      video.onloadedmetadata = () => {
        const duration = video.duration;
        URL.revokeObjectURL(url);

        if (!Number.isFinite(duration) || duration <= 0) {
          reject(new Error("Unable to read the video duration."));
          return;
        }

        if (duration > MAX_DURATION + 0.25) {
          reject(new Error(`Videos can only be ${MAX_DURATION} seconds or less.`));
          return;
        }

        resolve(duration);
      };

      video.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Unable to read this video."));
      };

      video.src = url;
    });
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setError("");

    if (!selectedFile.type.startsWith("video/")) {
      setError("Please select a video file.");
      e.target.value = "";
      return;
    }

    try {
      const duration = await checkUploadedVideoDuration(selectedFile);

      stopCamera();
      clearRecordingTimer();
      clearPreview();

      const url = URL.createObjectURL(selectedFile);

      setFile(selectedFile);
      setVideoSource("uploaded");
      setRecordedDuration(0);
      setPreviewDuration(duration);
      setPreviewUrl(url);
      setRecordingTime(0);
    } catch (err) {
      console.error("Video validation error:", err);

      setFile(null);
      setVideoSource(null);
      setError(err.message);
      e.target.value = "";
    }
  };

  const openCamera = async () => {
    try {
      setError("");
      clearPreview();

      setFile(null);
      setVideoSource(null);
      setRecordingTime(0);
      setRecordedDuration(0);

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      streamRef.current = stream;
      setCameraOpen(true);

      setTimeout(() => {
        const video = cameraVideoRef.current;
        if (!video) return;

        video.srcObject = stream;
        video.play().catch(() => {});
      }, 0);
    } catch (err) {
      console.error("Camera error:", err);
      setError("Unable to access your camera and microphone.");
    }
  };

  const getSupportedMimeType = () => {
    if (typeof MediaRecorder === "undefined") return "";

    const types = ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp9,opus", "video/webm"];

    return types.find((mimeType) => MediaRecorder.isTypeSupported(mimeType)) || "";
  };

  const stopRecording = () => {
    clearRecordingTimer();

    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state === "recording") {
      try {
        recorder.requestData();
      } catch (error) {
        console.error(error);
      }

      recorder.stop();
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;

    setError("");
    setRecordingTime(0);
    setRecordedDuration(0);

    const chunks = [];

    try {
      const mimeType = getSupportedMimeType();
      const recorder = mimeType ? new MediaRecorder(streamRef.current, { mimeType }) : new MediaRecorder(streamRef.current);

      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) chunks.push(event.data);
      };

      recorder.onstop = () => {
        try {
          const duration = recordingStartedAtRef.current ? Math.min((Date.now() - recordingStartedAtRef.current) / 1000, MAX_DURATION) : Math.min(recordingTime, MAX_DURATION);

          recordingStartedAtRef.current = null;

          const actualDuration = Math.max(0.1, duration);
          const recordedMimeType = recorder.mimeType || "video/webm";
          const blob = new Blob(chunks, { type: recordedMimeType });

          if (blob.size === 0) throw new Error("The recorded video is empty.");

          const recordedFile = new File([blob], `${isReply ? "reply" : "comment"}-video-${Date.now()}.webm`, { type: recordedMimeType });

          clearPreview();

          const url = URL.createObjectURL(recordedFile);

          setFile(recordedFile);
          setVideoSource("recorded");
          setRecordedDuration(actualDuration);
          setPreviewDuration(actualDuration);
          setPreviewCurrentTime(0);
          setPreviewUrl(url);
          setRecording(false);

          stopCamera();
        } catch (err) {
          console.error("Recorded video error:", err);

          setRecording(false);
          stopCamera();
          setError("Unable to process the recorded video.");
        }
      };

      recorder.onerror = (event) => {
        console.error("MediaRecorder error:", event);
        setError("Unable to record the video.");
      };

      recorder.start(250);
      recordingStartedAtRef.current = Date.now();
      setRecording(true);

      timerRef.current = setInterval(() => {
        setRecordingTime((previous) => {
          const next = previous + 1;

          if (next >= MAX_DURATION) {
            setTimeout(() => stopRecording(), 0);
            return MAX_DURATION;
          }

          return next;
        });
      }, 1000);
    } catch (err) {
      console.error("Recording error:", err);
      setError("Unable to start video recording.");
    }
  };

  const normalizePreviewDuration = () => {
    const video = previewVideoRef.current;
    if (!video) return;

    const duration = video.duration;

    if (Number.isFinite(duration) && duration > 0) {
      setPreviewDuration(duration);
      return;
    }

    if (videoSource === "recorded" && !Number.isFinite(duration)) {
      try {
        video.currentTime = 10000000;
      } catch (error) {
        console.error(error);
      }
    }
  };

  const handlePreviewDurationChange = () => {
    const video = previewVideoRef.current;
    if (!video) return;

    const duration = video.duration;
    if (!Number.isFinite(duration) || duration <= 0) return;

    setPreviewDuration(duration);

    if (video.currentTime > duration || video.currentTime > 1000) video.currentTime = 0;

    setPreviewCurrentTime(video.currentTime || 0);
  };

  const togglePreviewPlay = async () => {
    const video = previewVideoRef.current;
    if (!video) return;

    try {
      if (video.paused) {
        if (effectivePreviewDuration > 0 && previewCurrentTime >= effectivePreviewDuration - 0.05) video.currentTime = 0;

        await video.play();
        setIsPreviewPlaying(true);
      } else {
        video.pause();
        setIsPreviewPlaying(false);
      }
    } catch (err) {
      console.error("Preview playback error:", err);
    }
  };

  const handlePreviewTimeUpdate = () => {
    const video = previewVideoRef.current;
    if (!video) return;

    const current = video.currentTime;
    if (!Number.isFinite(current) || current < 0 || current > 1000) return;

    setPreviewCurrentTime(current);

    if (Number.isFinite(video.duration) && video.duration > 0) setPreviewDuration(video.duration);
  };

  const handlePreviewSeek = (value) => {
    const video = previewVideoRef.current;
    if (!video) return;

    const duration = effectivePreviewDuration;
    if (!Number.isFinite(duration) || duration <= 0) return;

    let newTime = Number(value);
    if (!Number.isFinite(newTime)) return;

    newTime = Math.max(0, Math.min(newTime, duration));

    try {
      video.currentTime = newTime;
      setPreviewCurrentTime(newTime);
    } catch (err) {
      console.error("Seek error:", err);
    }
  };

  const handlePreviewEnded = () => {
    setIsPreviewPlaying(false);
    setPreviewCurrentTime(0);

    if (previewVideoRef.current) {
      try {
        previewVideoRef.current.currentTime = 0;
      } catch (error) {
        console.error(error);
      }
    }
  };

  const removeVideo = () => {
    if (previewVideoRef.current) previewVideoRef.current.pause();

    clearPreview();

    setFile(null);
    setVideoSource(null);
    setRecordingTime(0);
    setRecordedDuration(0);
    setError("");

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const retakeVideo = async () => {
    removeVideo();
    await openCamera();
  };

  const cancelCamera = () => {
    clearRecordingTimer();

    const recorder = mediaRecorderRef.current;

    if (recorder && recorder.state === "recording") {
      recorder.onstop = null;

      try {
        recorder.stop();
      } catch (error) {
        console.error(error);
      }
    }

    recordingStartedAtRef.current = null;

    stopCamera();
    setRecording(false);
    setRecordingTime(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isReply && !comment?.id) {
      ToastMessage("Comment ID is missing.");
      return;
    }

    if (!file) {
      setError("Please select or record a video.");
      return;
    }

    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError("");

      if (videoSource === "uploaded") await checkUploadedVideoDuration(file);

      const formData = new FormData();

      formData.append("type", isReply ? type : postType);

      if (isReply) formData.append("comment_id", comment.id);
      else formData.append("post_id", postId);

      formData.append("content", file, file.name);

      const res = isReply ? await createReply(formData) : await createComment(formData);

      ToastMessage(res?.data?.message || (isReply ? "Your reply has been created!" : "Your comment has been created!"));

      removeVideo();

      if (isReply) closeReply?.();
      else close?.();
    } catch (err) {
      console.error(isReply ? "Create video reply error:" : "Create video comment error:", err);

      setError(err?.response?.data?.message || err?.message || (isReply ? "Unable to create your video reply." : "Unable to create your video comment."));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      clearRecordingTimer();

      const recorder = mediaRecorderRef.current;

      if (recorder && recorder.state === "recording") {
        recorder.onstop = null;

        try {
          recorder.stop();
        } catch (error) {
          console.error(error);
        }
      }

      if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    };
  }, []);

  const previewProgress = effectivePreviewDuration > 0 ? Math.min((previewCurrentTime / effectivePreviewDuration) * 100, 100) : 0;

  return (
    <div className={`w-full ${recording ? "flex min-h-[65vh] flex-col" : ""}`}>
      {/* TOP MESSAGE */}
      {!cameraOpen && !file && (
        <div className="mb-5 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-purple-50 text-[#6469ff]">
            <MdVideocam size={24} />
          </div>

          <h2 className="mt-3 text-base font-semibold text-gray-900">Video {isReply ? "reply" : "comment"}</h2>
          <p className="mt-1 text-sm text-gray-500">Choose a video or record one directly.</p>
          <p className="mt-1 text-xs font-medium text-purple-500">Maximum {MAX_DURATION} seconds</p>
        </div>
      )}

      <form onSubmit={handleSubmit} encType="multipart/form-data" className={recording ? "flex flex-1 flex-col" : ""}>
        {/* SOURCE */}
        {!file && !cameraOpen && (
          <div className="grid grid-cols-2 gap-3">
            <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-6 text-gray-600 transition hover:border-purple-300 hover:bg-purple-50 hover:text-purple-600">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100">
                <FaUpload size={16} />
              </div>

              <span className="text-sm font-semibold">Choose video</span>
              <span className="text-center text-xs text-gray-400">From your device</span>

              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileChange} />
            </label>

            <button type="button" onClick={openCamera} className="flex flex-col items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-6 text-gray-600 transition hover:border-purple-300 hover:bg-purple-50 hover:text-purple-600">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-100">
                <MdVideocam size={20} />
              </div>

              <span className="text-sm font-semibold">Record video</span>
              <span className="text-xs text-gray-400">Use camera</span>
            </button>
          </div>
        )}

        {/* CAMERA — 4:5 */}
        {cameraOpen && !file && (
          <div className={recording ? "flex flex-1 flex-col" : ""}>
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
              <video ref={cameraVideoRef} autoPlay muted playsInline className="absolute inset-0 h-full w-full object-cover" />

              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/35 to-transparent" />

              <button type="button" onClick={cancelCamera} className="absolute right-4 top-4 z-30 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-gray-700 shadow-md backdrop-blur hover:bg-white">
                <FaTimes size={14} />
              </button>

              {recording && (
                <div className="absolute left-4 top-4 z-30 flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-800 shadow backdrop-blur">
                  <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                  Recording
                </div>
              )}

              <div className="absolute bottom-7 left-1/2 z-30 -translate-x-1/2">
                {!recording ? (
                  <button type="button" onClick={startRecording} title="Start recording" className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-4 border-white bg-white/20 shadow-xl backdrop-blur-sm transition hover:scale-105">
                    <span className="h-[48px] w-[48px] rounded-full bg-red-500" />
                  </button>
                ) : (
                  <button type="button" onClick={stopRecording} title="Stop recording" className="flex h-[68px] w-[68px] items-center justify-center rounded-full border-4 border-white bg-white/20 shadow-xl backdrop-blur-sm">
                    <span className="h-7 w-7 rounded-md bg-red-500" />
                  </button>
                )}
              </div>

              <div className="absolute bottom-8 right-4 z-30 rounded-full bg-white/90 px-3 py-1.5 text-xs font-semibold text-gray-700 shadow backdrop-blur">
                {formatTime(recordingTime)} / 00:15
              </div>

              <div className="absolute bottom-0 left-0 z-30 h-1.5 w-full bg-white/40">
                <div className="h-full bg-purple-600 transition-all duration-1000" style={{ width: `${(recordingTime / MAX_DURATION) * 100}%` }} />
              </div>
            </div>
          </div>
        )}

        {/* PREVIEW — 4:5 */}
        {file && previewUrl && (
          <div>
            <div className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
              <video
                ref={previewVideoRef}
                src={previewUrl}
                playsInline
                onLoadedMetadata={normalizePreviewDuration}
                onDurationChange={handlePreviewDurationChange}
                onTimeUpdate={handlePreviewTimeUpdate}
                onEnded={handlePreviewEnded}
                className="absolute inset-0 h-full w-full object-cover"
              />

              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/35 to-transparent" />

              {!isPreviewPlaying && (
                <button type="button" onClick={togglePreviewPlay} className="absolute left-1/2 top-1/2 z-20 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-black/45 text-white shadow-xl backdrop-blur transition hover:scale-105 hover:bg-black/60">
                  <FaPlay size={21} className="ml-1" />
                </button>
              )}

              {isPreviewPlaying && (
                <button type="button" onClick={togglePreviewPlay} className="absolute left-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white shadow-md backdrop-blur hover:bg-black/60">
                  <FaPause size={14} />
                </button>
              )}

              <button type="button" onClick={removeVideo} disabled={isSubmitting} className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-black/45 text-white shadow-md backdrop-blur hover:bg-black/60 disabled:opacity-50">
                <FaTimes size={14} />
              </button>

              <div className="absolute bottom-8 left-4 z-20 rounded-full bg-black/45 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
                {formatTime(previewCurrentTime)} / {formatTime(effectivePreviewDuration)}
              </div>

              <input
                type="range"
                min={0}
                max={effectivePreviewDuration || 0}
                step={0.01}
                value={Math.min(previewCurrentTime, effectivePreviewDuration || 0)}
                onChange={(e) => handlePreviewSeek(e.target.value)}
                className="absolute bottom-0 left-0 z-30 h-3 w-full cursor-pointer appearance-none [&::-webkit-slider-runnable-track]:h-1.5 [&::-webkit-slider-runnable-track]:rounded-full [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-thumb]:mt-[-4px] [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-600 [&::-webkit-slider-thumb]:shadow-md [&::-moz-range-track]:h-1.5 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-moz-range-thumb]:h-3.5 [&::-moz-range-thumb]:w-3.5 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-purple-600"
                style={{ background: `linear-gradient(to right, #7c3aed 0%, #7c3aed ${previewProgress}%, rgba(255,255,255,.55) ${previewProgress}%, rgba(255,255,255,.55) 100%)` }}
              />
            </div>

            <button type="button" onClick={retakeVideo} disabled={isSubmitting} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-2.5 text-sm font-medium text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
              <MdRefresh size={18} />
              Record again
            </button>
          </div>
        )}

        {error && <div className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-500">{error}</div>}

        {file && (
          <button type="submit" disabled={isSubmitting} className="mt-4 w-full rounded-xl bg-[#6469ff] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#555be8] disabled:cursor-not-allowed disabled:opacity-50">
            {isSubmitting ? "Sending..." : isReply ? "Reply" : "Post video comment"}
          </button>
        )}
      </form>
    </div>
  );
};

export default VideoComposer;